import { useState, useEffect } from "react";
import {
  PromptInput,
  PromptInputAttachment,
  PromptInputAttachments,
  PromptInputBody,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputToolbar,
} from "@/components/ai-elements/prompt-input";
import { ArrowLeft } from "lucide-react";
import {
  useSendQueryToChatMutation,
  useCreateConversationMutation,
  useUpdateConversationByIdMutation,
} from "@/redux/api/conversations_new.rtkq";
import type { ChatHistory } from "../../types";
import { toast } from "sonner";
import MimicLoader from "@/components/conversation/utils/MimicLoader";
import InsufficientCreditsDialog from "@/components/InsufficientCreditsDialog";
import { trackWallShown } from "@/lib/mixpanel";
import { cn } from "@/lib/utils";
import { useNavigate, useParams } from "react-router";

const SendQueryToChat = ({
  suggestionQuestion,
  isFollowupQuery = false,
  chatHistory = [],
  disable,
}: {
  suggestionQuestion?: string | null;
  isFollowupQuery?: boolean;
  chatHistory?: ChatHistory[];
  disable?: boolean;
}) => {
  const [insufficientCreditsOpen, setInsufficientCreditsOpen] = useState(false);
  const [creditWall, setCreditWall] = useState<{
    code?: string;
    appId?: string;
    required?: number;
    available?: number;
    quotaWindowEndsAt?: string | null;
  } | null>(null);
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [question, setQuestion] = useState("");
  const [sendQueryToChat, { data: chatAns, isLoading, isSuccess, isError, error, reset: resetSendQuery }] =
    useSendQueryToChatMutation();
  const [
    updateConversationById,
    {
      isLoading: isUpdatingConversation,
      isError: isUpdatingConversationError,
      isSuccess: isUpdatingConversationSuccess,
      reset: resetUpdatingConversation,
    },
  ] = useUpdateConversationByIdMutation();
  const [
    createConversation,
    {
      data: createConversationData,
      error: createConversationError,
      isLoading: createConversationLoading,
      isError: createConversationIsError,
      isSuccess: createConversationIsSuccess,
      reset: resetCreateConversation,
    },
  ] = useCreateConversationMutation();

  const handleSend = async () => {
    if (!question.trim()) return;
    sendQueryToChat({ message: question, chatHistory });
  };

  useEffect(() => {
    if (suggestionQuestion) {
      setQuestion(suggestionQuestion);
    }
  }, [suggestionQuestion]);

  useEffect(() => {
    // console.log("🔑 [SEND QUERY TO CHAT] chatAns", chatAns);
    if (isSuccess && chatAns) {
      if (isFollowupQuery && conversationId) {
        updateConversationById({
          conversationId: conversationId as string,
          newMessages: chatAns.chat_history.slice(-2) as [ChatHistory, ChatHistory],
        });
      } else {
        createConversation({ chatHistory: chatAns.chat_history });
      }
      // Reset the sendQuery mutation to prevent re-execution
      resetSendQuery();
    }
  }, [isSuccess, createConversation, chatAns, conversationId, updateConversationById, isFollowupQuery, resetSendQuery]);

  useEffect(() => {
    if (isError && error) {
      const errData = (error as any)?.data;
      const code = errData?.code as string | undefined;
      const wall =
        code === "USAGE_WINDOW_EXHAUSTED"
          ? "window_exhausted"
          : code === "INSUFFICIENT_CREDITS"
            ? "insufficient_credits"
            : code === "SUBSCRIPTION_EXPIRED"
              ? "subscription_blocked"
              : code;
      if (code === "INSUFFICIENT_CREDITS" || code === "USAGE_WINDOW_EXHAUSTED") {
        setCreditWall(errData);
        setInsufficientCreditsOpen(true);
        trackWallShown({
          wall,
          app: errData?.appId ?? "ai-assistant",
          required: errData?.required,
          available: errData?.available,
          window_ends_at: errData?.quotaWindowEndsAt,
          surface: "web",
          feature: "assistant_chat",
          ui: "dialog",
          message_key: code,
        });
      } else if (code === "SUBSCRIPTION_EXPIRED") {
        trackWallShown({
          wall,
          surface: "web",
          feature: "assistant_chat",
          ui: "toast",
          message_key: code,
        });
        toast.error("הגישה למנוי חסומה", {
          description: "פנו לתמיכה כדי להמשיך להשתמש בסוכן",
          duration: 8000,
        });
      } else {
        trackWallShown({
          wall,
          surface: "web",
          feature: "assistant_chat",
          ui: "generic_error",
          message_key: code ?? "generic",
        });
        toast.error("שגיאה בשליחת השאלה", {
          description: (error as any)?.error || "נסה שוב מאוחר יותר",
          duration: 5000,
        });
      }
    }
  }, [isError, error]);

  useEffect(() => {
    if (createConversationIsSuccess && createConversationData) {
      toast.success(`השיחה ${createConversationData.conversation_name} נוצרה בהצלחה`);
      navigate(`/assistant/${createConversationData.conversation_id}`, { replace: true });
      // Reset the createConversation mutation to prevent re-execution
      resetCreateConversation();
    }
  }, [createConversationIsSuccess, createConversationData, navigate, resetCreateConversation]);

  useEffect(() => {
    if (createConversationIsError && createConversationError) {
      toast.error("שגיאה ביצירת השיחה", {
        description: "נסה שוב מאוחר יותר",
        duration: 5000,
      });
    }
  }, [createConversationError, createConversationIsError]);

  useEffect(() => {
    if (isUpdatingConversationSuccess) {
      setQuestion("");
      resetUpdatingConversation();
      toast.success("השיחה עודכנה בהצלחה");
    }
  }, [isUpdatingConversationSuccess, resetUpdatingConversation]);

  useEffect(() => {
    if (isUpdatingConversationError) {
      toast.error("שגיאה בעדכון השיחה", {
        description: "נסה שוב מאוחר יותר",
        duration: 5000,
      });
    }
  }, [isUpdatingConversationError]);

  const isLoadingState = isLoading || createConversationLoading || isUpdatingConversation;

  return (
    <div
      className={cn(
        "mt-6 md:mt-8 flex w-full justify-center px-2 max-w-[800px]",
        isFollowupQuery && "absolute bottom-0 left-0 right-0 pb-[env(safe-area-inset-bottom)]"
      )}
    >
      <PromptInput onSubmit={handleSend} className={cn(isFollowupQuery && "m-4")} dir="rtl">
        <PromptInputBody>
          <PromptInputAttachments>{(attachment) => <PromptInputAttachment data={attachment} />}</PromptInputAttachments>
          <PromptInputTextarea
            onChange={(e) => setQuestion(e.target.value)}
            disabled={disable}
            value={question}
            aria-label="שאל את שאלתך"
            placeholder={isFollowupQuery ? "שאל שאלת המשך" : "שאל את שאלתך"}
            className={cn("md:text-base", !isFollowupQuery && "max-h-35 min-h-35")}
          />
        </PromptInputBody>
        <PromptInputToolbar>
          <PromptInputSubmit
            disabled={isLoadingState || disable}
            status={"ready"}
            size="lg"
            className="m-2 relative rounded-[100px] text-white justify-center gap-2 overflow-visible shadow-[0_4.273px_12.819px_-4.273px_#789DF6] bg-gradient-to-r from-blue-400 to-indigo-500 hover:bg-gradient-to-r hover:from-blue-500 hover:to-indigo-600 active:scale-95 transition-all duration-100 hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-[16px] tracking-[0.03em]">שלח</span>
            <ArrowLeft className="size-4 " />
          </PromptInputSubmit>
        </PromptInputToolbar>
      </PromptInput>
      <MimicLoader isLoading={isLoadingState} />
      <InsufficientCreditsDialog
        open={insufficientCreditsOpen}
        onOpenChange={setInsufficientCreditsOpen}
        description="פנו אלינו להשלמת יתרה לסוכן המשפטי."
        wall={
          creditWall?.code === "USAGE_WINDOW_EXHAUSTED"
            ? "window_exhausted"
            : "insufficient_credits"
        }
        quotaWindowEndsAt={creditWall?.quotaWindowEndsAt}
      />
    </div>
  );
};

export default SendQueryToChat;
